/**
 * platformWiseColumnConfig
 * -------------------------
 * Stores table column configurations for each platform (e.g., amazon, myntra, flipkart).
 * Each platform maps to an array of column config objects.
 *
 * Column Config Properties:
 * - key: (string) Unique identifier for the column, usually matching a backend field.
 * - innerKeys: (object) Sub-mappings for rendering complex columns.
 *    - image: Field used for product image.
 *    - productName: Field used for product name.
 *    - asin: { label: string, value: string } → Display label + backend field for ASIN.
 * - title: (string) Tooltip title for the column.
 * - shortTitle: (string) Full display name for UI and exports.
 * - format: (string) Data type → "string", "number", "percent", "currency", etc.
 * - isSingleColumn: (boolean) If true, the column is merged into a single column in comparison downloads.
 * - dataIndex: (string) Backend mapping key (used for fetching data) — may be deprecated in future.
 * - isSticky: (boolean) If true, column stays fixed when scrolling horizontally.
 * - stickyLeft: (number) Left pixel offset for sticky placement.
 * - minWidth: (number) Minimum column width (in px).
 * - isDimension: (boolean) Marks dimension columns (non-metric fields like product name, category).
 * - viewType: (array) Determines visibility in different views: 
 *    ["DRR", "AGGREGATE", "FUNNEL", "PERFORMANCE", "ORDER"].
 *    Column will be shown if view matches any item in this array.
 * - alignment: (string) Text alignment inside the column → "left", "center", "right".
 * - colSpan: (number) Number of columns to span (for comparison header).
 *
 * Always update configs when:
 * - Adding or removing metrics/dimensions.
 * - Changing sticky column placement.
 * - Updating export/download requirements.
 * - Adding new view types.
 */

export const platformWiseExtraColumnsConfig = {
  amazon: [
    {
      key: "VariantAnalysisTablePPMPlaceholder.packType",
      title: "Pack Type",
      shortTitle: "Pack Type",
      type: "string",
      isSticky: true,
      isSingleColumn: true,
      dataIndex: "VariantAnalysisTablePPMPlaceholder.packType",
      minWidth: 160,
      isDimension: true,
      viewType: ["DRR", "AGGREGATE", "FUNNEL", "PERFORMANCE", "ORDER"],
      alignment: "left",
      colSpan: 1
    },
    {
      key: "VariantAnalysisTablePPMPlaceholder.product_tags",
      title: "Product Tag 1",
      shortTitle: "Product Tag 1",
      type: "string",
      isSticky: true,
      isSingleColumn: true,
      dataIndex: "VariantAnalysisTablePPMPlaceholder.product_tags",
      minWidth: 160,
      isDimension: true,
      viewType: ["DRR", "AGGREGATE", "FUNNEL", "PERFORMANCE", "ORDER"],
      alignment: "left",
      colSpan: 1
    },
    ...[2,3,4,5,6,7,8,9,10].map((index) => ({
      key: `VariantAnalysisTablePPMPlaceholder.product_tags_${index}`,
      title: `Product Tag ${index}`,
      shortTitle: `Product Tag ${index}`,
      type: "string",
      isSticky: true,
      isSingleColumn: true,
      dataIndex: `VariantAnalysisTablePPMPlaceholder.product_tags_${index}`,
      minWidth: 160,
      isDimension: true,
      viewType: ["DRR", "AGGREGATE", "FUNNEL", "PERFORMANCE", "ORDER"],
      alignment: "left",
      colSpan: 1
    })),
  ],
  myntra: [
    {
      key: "article_type",
      title: "Article",
      shortTitle: "Article",
      type: "string",
      isSingleColumn: true,
      dataIndex: "article_type",
      minWidth: 160,
      isSticky: true,
      isDimension: true,
      viewType: ["DRR", "AGGREGATE", "FUNNEL", "PERFORMANCE", "ORDER"],
      alignment: "left",
      colSpan: 1
    },
    {
      key: "collection",
      title: "Collection",
      shortTitle: "Collection",
      type: "string",
      isSticky: true,
      isSingleColumn: true,
      dataIndex: "collection",
      minWidth: 160,
      isDimension: true,
      viewType: ["DRR", "AGGREGATE", "FUNNEL", "PERFORMANCE", "ORDER"],
      alignment: "left",
      colSpan: 1
    },
    {
      key: "category",
      title: "Category",
      shortTitle: "Category",
      type: "string",
      isSticky: true,
      isSingleColumn: true,
      dataIndex: "category",
      minWidth: 160,
      isDimension: true,
      viewType: ["DRR", "AGGREGATE", "FUNNEL", "PERFORMANCE", "ORDER"],
      alignment: "left",
      colSpan: 1
    },
    {
      key: "brand",
      title: "Brand",
      shortTitle: "Brand",
      type: "string",
      isSticky: true,
      isSingleColumn: true,
      dataIndex: "brand",
      minWidth: 140,
      isDimension: true,
      viewType: ["DRR", "AGGREGATE", "FUNNEL", "PERFORMANCE", "ORDER"],
      alignment: "left",
      colSpan: 1
    },
    {
      key: "size",
      title: "Size",
      shortTitle: "Size",
      type: "string",
      isSticky: true,
      isSingleColumn: true,
      dataIndex: "size",
      minWidth: 120,
      isDimension: true,
      viewType: ["DRR", "AGGREGATE", "FUNNEL", "PERFORMANCE", "ORDER"],
      alignment: "center",
      colSpan: 1
    },
    {
      key: "seller",
      title: "Seller",
      shortTitle: "Seller",
      type: "string",
      isSticky: true,
      isSingleColumn: true,
      dataIndex: "seller",
      minWidth: 160,
      isDimension: true,
      viewType: ["DRR", "AGGREGATE", "FUNNEL", "PERFORMANCE", "ORDER"],
      alignment: "left",
      colSpan: 1
    },
    {
      key: "gender",
      title: "Gender",
      shortTitle: "Gender",
      type: "string",
      isSticky: true,
      isSingleColumn: true,
      dataIndex: "gender",
      minWidth: 120,
      isDimension: true,
      viewType: ["DRR", "AGGREGATE", "FUNNEL", "PERFORMANCE", "ORDER"],
      alignment: "center",
      colSpan: 1
    },
    {
      key: "color",
      title: "Color",
      shortTitle: "Color",
      type: "string",
      isSticky: true,
      isSingleColumn: true,
      dataIndex: "color",
      minWidth: 140,
      isDimension: true,
      viewType: ["DRR", "AGGREGATE", "FUNNEL", "PERFORMANCE", "ORDER"],
      alignment: "left",
      colSpan: 1
    },
    {
      key: "channel",
      title: "Channel",
      shortTitle: "Channel",
      type: "string",
      isSticky: true,
      isSingleColumn: true,
      dataIndex: "channel",
      minWidth: 140,
      isDimension: true,
      viewType: ["DRR", "AGGREGATE", "FUNNEL", "PERFORMANCE", "ORDER"],
      alignment: "left",
      colSpan: 1
    },
    {
      key: "product_tags",
      title: "Product Tag 1",
      shortTitle: "Product Tag 1",
      type: "string",
      isSticky: true,
      isSingleColumn: true,
      dataIndex: "product_tags",
      minWidth: 160,
      isDimension: true,
      viewType: ["DRR", "AGGREGATE", "FUNNEL", "PERFORMANCE", "ORDER"],
      alignment: "left",
      colSpan: 1
    },
    ...[2,3,4,5,6,7,8,9,10].map((index) => ({
      key: `product_tags_${index}`,
      title: `Product Tag ${index}`,
      shortTitle: `Product Tag ${index}`,
      type: "string",
      isSticky: true,
      isSingleColumn: true,
      dataIndex: `product_tags_${index}`,
      minWidth: 160,
      isDimension: true,
      viewType: ["DRR", "AGGREGATE", "FUNNEL", "PERFORMANCE", "ORDER"],
      alignment: "left",
      colSpan: 1
    })),
  ],
  flipkart: [
    {
      key: "article_type",
      title: "Article",
      shortTitle: "Article",
      type: "string",
      isSticky: true,
      isSingleColumn: true,
      dataIndex: "article_type",
      minWidth: 160,
      isDimension: true,
      viewType: ["DRR", "AGGREGATE", "FUNNEL", "PERFORMANCE", "ORDER"],
      alignment: "left",
      colSpan: 1
    },
    {
      key: "collection",
      title: "Collection",
      shortTitle: "Collection",
      type: "string",
      isSticky: true,
      isSingleColumn: true,
      dataIndex: "collection",
      minWidth: 160,
      isDimension: true,
      viewType: ["DRR", "AGGREGATE", "FUNNEL", "PERFORMANCE", "ORDER"],
      alignment: "left",
      colSpan: 1
    },
    {
      key: "category",
      title: "Category",
      shortTitle: "Category",
      type: "string",
      isSticky: true,
      isSingleColumn: true,
      dataIndex: "category",
      minWidth: 160,
      isDimension: true,
      viewType: ["DRR", "AGGREGATE", "FUNNEL", "PERFORMANCE", "ORDER"],
      alignment: "left",
      colSpan: 1
    },
    {
      key: "brand",
      title: "Brand",
      shortTitle: "Brand",
      type: "string",
      isSticky: true,
      isSingleColumn: true,
      dataIndex: "brand",
      minWidth: 140,
      isDimension: true,
      viewType: ["DRR", "AGGREGATE", "FUNNEL", "PERFORMANCE", "ORDER"],
      alignment: "left",
      colSpan: 1
    },
    {
      key: "size",
      title: "Size",
      shortTitle: "Size",
      type: "string",
      isSticky: true,
      isSingleColumn: true,
      dataIndex: "size",
      minWidth: 120,
      isDimension: true,
      viewType: ["DRR", "AGGREGATE", "FUNNEL", "PERFORMANCE", "ORDER"],
      alignment: "center",
      colSpan: 1
    },
    {
      key: "seller",
      title: "Seller",
      shortTitle: "Seller",
      type: "string",
      isSticky: true,
      isSingleColumn: true,
      dataIndex: "seller",
      minWidth: 160,
      isDimension: true,
      viewType: ["DRR", "AGGREGATE", "FUNNEL", "PERFORMANCE", "ORDER"],
      alignment: "left",
      colSpan: 1
    },
    {
      key: "gender",
      title: "Gender",
      shortTitle: "Gender",
      type: "string",
      isSticky: true,
      isSingleColumn: true,
      dataIndex: "gender",
      minWidth: 120,
      isDimension: true,
      viewType: ["DRR", "AGGREGATE", "FUNNEL", "PERFORMANCE", "ORDER"],
      alignment: "center",
      colSpan: 1
    },
    {
      key: "color",
      title: "Color",
      shortTitle: "Color",
      type: "string",
      isSticky: true,
      isSingleColumn: true,
      dataIndex: "color",
      minWidth: 140,
      isDimension: true,
      viewType: ["DRR", "AGGREGATE", "FUNNEL", "PERFORMANCE", "ORDER"],
      alignment: "left",
      colSpan: 1
    },
    {
      key: "channel",
      title: "Channel",
      shortTitle: "Channel",
      type: "string",
      isSticky: true,
      isSingleColumn: true,
      dataIndex: "channel",
      minWidth: 140,
      isDimension: true,
      viewType: ["DRR", "AGGREGATE", "FUNNEL", "PERFORMANCE", "ORDER"],
      alignment: "left",
      colSpan: 1
    },
    {
      key: "product_tags",
      title: "Product Tag 1",
      shortTitle: "Product Tag 1",
      type: "string",
      isSticky: true,
      isSingleColumn: true,
      dataIndex: "product_tags",
      minWidth: 160,
      isDimension: true,
      viewType: ["DRR", "AGGREGATE", "FUNNEL", "PERFORMANCE", "ORDER"],
      alignment: "left",
      colSpan: 1
    },
    ...[2,3,4,5,6,7,8,9,10].map((index) => ({
      key: `product_tags_${index}`,
      title: `Product Tag ${index}`,
      shortTitle: `Product Tag ${index}`,
      type: "string",
      isSticky: true,
      isSingleColumn: true,
      dataIndex: `product_tags_${index}`,
      minWidth: 160,
      isDimension: true,
      viewType: ["DRR", "AGGREGATE", "FUNNEL", "PERFORMANCE", "ORDER"],
      alignment: "left",
      colSpan: 1
    })),
  ],
  
}

export const platformWiseColumnConfig = {
  amazon: [
     {
      key: "VariantAnalysisTablePPMPlaceholder.asin",
      innerKeys: {
        image: "VariantAnalysisTablePPMPlaceholder.image",
        productName: "VariantAnalysisTablePPMPlaceholder.name",
        asin: {label:"ASIN", value:"VariantAnalysisTablePPMPlaceholder.asin"},
      },
      title: "Child Product (ASIN)",
      shortTitle: "Child Product (ASIN)",
      type: "string",
      isSingleColumn: true,
      dataIndex: "VariantAnalysisTablePPMPlaceholder.asin",
      isSticky: true,
      stickyLeft: 0,
      minWidth: 250,
      isDimension: true,
      viewType: ["DRR","AGGREGATE", "FUNNEL", "PERFORMANCE", "ORDER"],
      alignment:"left",
      colSpan: 1,
    },
    // {
    //   key: "VariantAnalysisTablePPMPlaceholder.asin",
    //   title: "Variant Analysis Table Asin",
    //   shortTitle: "Asin",
    //   type: "string",
    //   isSingleColumn: true,
    //   dataIndex: "VariantAnalysisTablePPMPlaceholder.asin",
    //   isSticky: true,
    //   stickyLeft: 0,
    //   minWidth: 120,
    //   isDimension: true,
    //   viewType: ["DRR","AGGREGATE", "FUNNEL", "PERFORMANCE"]
    // },
    {
      key: "VariantAnalysisTablePPMPlaceholder.parentSku",
      title: "Parent Product (ASIN)",
      shortTitle: "Parent Product (ASIN)",
      type: "string",
      isSingleColumn: true,
      dataIndex: "VariantAnalysisTablePPMPlaceholder.parentSku",
      isSticky: true,
      stickyLeft: 0,
      minWidth: 150,
      isDimension: true,
      viewType: ["DRR","AGGREGATE", "FUNNEL", "PERFORMANCE", "ORDER"],
      alignment:"left",
      colSpan: 1,
    },
    {
      key: "VariantAnalysisTablePPMPlaceholder.broadCategory",
      title: "Variant Analysis Table Broad Category",
      shortTitle: "Broad Category",
      type: "string",
      isSingleColumn: true,
      dataIndex: "VariantAnalysisTablePPMPlaceholder.broadCategory",
      isSticky: true,
      stickyLeft: 0,
      minWidth: 150,
      isDimension: true,
      viewType: ["DRR","AGGREGATE", "FUNNEL", "PERFORMANCE", "ORDER"],
      alignment:"left",
      colSpan: 1,

    },
    {
      key: "VariantAnalysisTablePPMPlaceholder.category",
      title: "Variant Analysis Table Category",
      shortTitle: "Detailed Category",
      type: "string",
      isSingleColumn: true,
      dataIndex: "VariantAnalysisTablePPMPlaceholder.category",
      isSticky: true,
      stickyLeft: 0,
      minWidth: 150,
      isDimension: true,
      viewType: ["DRR","AGGREGATE", "FUNNEL", "PERFORMANCE", "ORDER"],
      alignment:"left",
      colSpan: 1,

    },
    ...platformWiseExtraColumnsConfig["amazon"],
    {
      key: "VariantAnalysisTablePPMPlaceholder.cost",
      type: "number",
      dataIndex: "VariantAnalysisTablePPMPlaceholder.cost",
      title: "Variant Analysis Table spends",
      shortTitle: "Spends",
      isCurrencyFormat: true,
      maximumFractionDigits: 0,
      viewType: ["AGGREGATE","PERFORMANCE", "FUNNEL", "ORDER"],
      colSpan: 2,
    },
    {
      key: "VariantAnalysisTablePPMPlaceholder.impressions",
      type: "number",
      dataIndex: "VariantAnalysisTablePPMPlaceholder.impressions",
      title: "Variant Analysis Table Impressions",
      shortTitle: "Impressions",
      viewType: ["AGGREGATE", "FUNNEL", "PERFORMANCE"],
      colSpan: 2,
    },
    // {
    //   key: "VariantAnalysisTablePPMPlaceholder.costDRR",
    //   type: "number",
    //   dataIndex: "VariantAnalysisTablePPMPlaceholder.costDRR",
    //   title: "Variant Analysis Table spends (DRR)",
    //   shortTitle: "Spends (DRR)",
    //   maximumFractionDigits: 0,
    //   isCurrencyFormat: true,
    //   viewType: ["DRR"],
    //   colSpan: 2,
    // },
    // {
    //   key: "VariantAnalysisTablePPMPlaceholder.impressionsDRR",
    //   type: "number",
    //   dataIndex: "VariantAnalysisTablePPMPlaceholder.impressionsDRR",
    //   title: "Variant Analysis Table Impressions (DRR)",
    //   shortTitle: "Impressions (DRR)",
    //   maximumFractionDigits: 0,
    //   viewType: ["DRR"],
    //   colSpan: 2,
    // },
     {
      key: "VariantAnalysisTablePPMPlaceholder.cpm",
      type: "number",
      dataIndex: "VariantAnalysisTablePPMPlaceholder.cpm",
      title: "Variant Analysis Table CPM",
      shortTitle: "CPM",
      viewType: ["AGGREGATE", "PERFORMANCE"],
      isDerivedValue: true,
      colSpan: 2,
    },
    {
      key: "VariantAnalysisTablePPMPlaceholder.clicks",
      type: "number",
      dataIndex: "VariantAnalysisTablePPMPlaceholder.clicks",
      title: "Variant Analysis Table clicks",
      shortTitle: "Clicks",
      viewType: ["AGGREGATE", "FUNNEL", "PERFORMANCE"],
      colSpan: 2,
    },
    // {
    //   key: "VariantAnalysisTablePPMPlaceholder.clicksDRR",
    //   type: "number",
    //   dataIndex: "VariantAnalysisTablePPMPlaceholder.clicksDRR",
    //   title: "Variant Analysis Table clicks (DRR)",
    //   shortTitle: "Clicks (DRR)",
    //   maximumFractionDigits: 0,
    //   viewType: ["DRR"],
    //   colSpan: 2,
    // },
    {
      key: "VariantAnalysisTablePPMPlaceholder.ctr",
      type: "number",
      format: "percent",
      dataIndex: "VariantAnalysisTablePPMPlaceholder.ctr",
      title: "Variant Analysis Table CTR",
      shortTitle: "CTR",
      viewType: ["AGGREGATE", "FUNNEL", "PERFORMANCE"],
      isDerivedValue: true,
      colSpan: 2,
    },
    {
      key: "VariantAnalysisTablePPMPlaceholder.cpc",
      type: "number",
      dataIndex: "VariantAnalysisTablePPMPlaceholder.cpc",
      title: "Variant Analysis Table CPC",
      shortTitle: "CPC",
      viewType: ["AGGREGATE", "PERFORMANCE"],
      isDerivedValue: true,
      colSpan: 2,
    },
    // {
    //   key: "VariantAnalysisTablePPMPlaceholder.ctrDRR",
    //   type: "number",
    //   format: "percent",
    //   dataIndex: "VariantAnalysisTablePPMPlaceholder.ctrDRR",
    //   title: "Variant Analysis Table CTR (DRR)",
    //   shortTitle: "CTR (DRR)",
    //   viewType: ["DRR"],
    //   isDerivedValue: true,
    //   colSpan: 2,
    // },
    // {
    //   key: "VariantAnalysisTablePPMPlaceholder.cpcDRR",
    //   type: "number",
    //   dataIndex: "VariantAnalysisTablePPMPlaceholder.cpcDRR",
    //   title: "Variant Analysis Table CPC (DRR)",
    //   shortTitle: "CPC (DRR)",
    //   viewType: ["DRR"],
    //   isDerivedValue: true,
    //   colSpan: 2,
    // },
    // {
    //   key: "VariantAnalysisTablePPMPlaceholder.conversion_rateDRR",
    //   type: "number",
    //   format: "percent",
    //   dataIndex: "VariantAnalysisTablePPMPlaceholder.conversion_rateDRR",
    //   title: "Variant Analysis Table conversion_rate (DRR)",
    //   shortTitle: "CVR (DRR)",
    //   viewType: ["DRR"],
    //   isDerivedValue: true,
    //   colSpan: 2,
    // },
    // {
    //   key: "VariantAnalysisTablePPMPlaceholder.cpmDRR",
    //   type: "number",
    //   dataIndex: "VariantAnalysisTablePPMPlaceholder.cpmDRR",
    //   title: "Variant Analysis Table CPM (DRR)",
    //   shortTitle: "CPM (DRR)",
    //   viewType: ["DRR"],
    //   isDerivedValue: true,
    //   colSpan: 2,
    // },
      {
      key: "VariantAnalysisTablePPMPlaceholder.conversion_rate",
      type: "number",
      format: "percent",
      dataIndex: "VariantAnalysisTablePPMPlaceholder.conversion_rate",
      title: "Variant Analysis Table conversion_rate",
      shortTitle: "CVR",
      viewType: ["AGGREGATE", "FUNNEL"],
      isDerivedValue: true,
      colSpan: 2,
    },
    {
      key: "VariantAnalysisTablePPMPlaceholder.conversions14d",
      type: "number",
      dataIndex: "VariantAnalysisTablePPMPlaceholder.conversions14d",
      title: "Variant Analysis Table orders",
      shortTitle: "Ad Units",
      viewType: ["AGGREGATE", "PERFORMANCE", "ORDER", "FUNNEL"],
      colSpan: 2,
    },
    {
      key: "VariantAnalysisTablePPMPlaceholder.tunits_sold",
      type: "number",
      dataIndex: "VariantAnalysisTablePPMPlaceholder.tunits_sold",
      title: "Total Units",
      shortTitle: "Total Units",
      viewType: ["AGGREGATE","PERFORMANCE", "ORDER", "FUNNEL"],
      colSpan: 2,
    },
    // {
    //   key: "VariantAnalysisTablePPMPlaceholder.conversions14dDRR",
    //   type: "number",
    //   dataIndex: "VariantAnalysisTablePPMPlaceholder.conversions14dDRR",
    //   title: "Variant Analysis Table Orders (DRR)",
    //   shortTitle: "Ad Units (DRR)",
    //   maximumFractionDigits: 0,
    //   viewType: ["DRR"],
    //   colSpan: 2,
    // },
    {
      key: "VariantAnalysisTablePPMPlaceholder.sales14d",
      type: "number",
      dataIndex: "VariantAnalysisTablePPMPlaceholder.sales14d",
      title: "Variant Analysis Table revenue",
      shortTitle: "Revenue",
      isCurrencyFormat: true,
      maximumFractionDigits: 0,
      viewType: ["AGGREGATE", "PERFORMANCE"],
      colSpan: 2,
    },
    {
      key: "VariantAnalysisTablePPMPlaceholder.trevnue",
      type: "number",
      dataIndex: "VariantAnalysisTablePPMPlaceholder.trevnue",
      title: "Total Revenue",
      shortTitle: "Total Revenue",
      isCurrencyFormat: true,
      maximumFractionDigits: 0,
      viewType: ["AGGREGATE", "PERFORMANCE", "FUNNEL"],
      colSpan: 2,
    },
    // {
    //   key: "VariantAnalysisTablePPMPlaceholder.sales14dDRR",
    //   type: "number",
    //   dataIndex: "VariantAnalysisTablePPMPlaceholder.sales14dDRR",
    //   title: "Variant Analysis Table revenue (DRR)",
    //   shortTitle: "Revenue (DRR)",
    //   isCurrencyFormat: true,
    //   maximumFractionDigits: 0,
    //   viewType: ["DRR"],
    //   colSpan: 2,
    // },
    {
      key: "VariantAnalysisTablePPMPlaceholder.average_order_value",
      type: "number",
      dataIndex: "VariantAnalysisTablePPMPlaceholder.average_order_value",
      title: "Variant Analysis Table AOV",
      shortTitle: "Average Order Value",
      viewType: ["AGGREGATE", "ORDER", "PERFORMANCE"],
      isDerivedValue: true,
      colSpan: 2,
    },
     {
      key: "VariantAnalysisTablePPMPlaceholder.MaximumRetailPrice",
      type: "number",
      isSingleColumn: true,
      dataIndex: "VariantAnalysisTablePPMPlaceholder.MaximumRetailPrice",
      title: "Maximum Retail Price",
      shortTitle: "MRP",
      isCurrencyFormat: true,
      viewType: ["AGGREGATE", "ORDER"],
      colSpan: 1,
    },
    {
      key: "VariantAnalysisTablePPMPlaceholder.roas",
      type: "number",
      dataIndex: "VariantAnalysisTablePPMPlaceholder.roas",
      title: "Variant Analysis Table ROAS",
      shortTitle: "ROAS",
      maximumFractionDigits: 1,
      viewType: ["AGGREGATE","PERFORMANCE", "ORDER"],
      isDerivedValue: true,
      colSpan: 2,
    },
     {
      key: "VariantAnalysisTablePPMPlaceholder.troas",
      type: "number",
      dataIndex: "VariantAnalysisTablePPMPlaceholder.troas",
      title: "Total ROAS",
      maximumFractionDigits: 1,
      shortTitle: "Total ROAS",
      viewType: ["AGGREGATE", "PERFORMANCE", "FUNNEL"],
      isDerivedValue: true,
      colSpan: 2,
    },
    // {
    //   key: "VariantAnalysisTablePPMPlaceholder.average_order_valueDRR",
    //   type: "number",
    //   dataIndex: "VariantAnalysisTablePPMPlaceholder.average_order_valueDRR",
    //   title: "Variant Analysis Table AOV (DRR)",
    //   shortTitle: "Average Order Value (DRR)",
    //   viewType: ["DRR"],
    //   isDerivedValue: true,
    //   colSpan: 2,
    // },
    {
      key: "VariantAnalysisTablePPMPlaceholder.acos",
      type: "number",
      format: "percent",
      dataIndex: "VariantAnalysisTablePPMPlaceholder.acos",
      title: "Variant Analysis Table ACOS",
      shortTitle: "ACOS",
      maximumFractionDigits: 0,
      viewType: ["AGGREGATE", "ORDER", "PERFORMANCE"],
      isDerivedValue: true,
      colSpan: 2,
    },
      {
      key: "VariantAnalysisTablePPMPlaceholder.tacos",
      type: "number",
      dataIndex: "VariantAnalysisTablePPMPlaceholder.tacos",
      title: "Total Advertising Cost of Sales (TACOS)",
      shortTitle: "Total ACOS",
      format: "percent",
      maximumFractionDigits: 0,
      viewType: ["AGGREGATE", "PERFORMANCE", "FUNNEL", "ORDER"],
      isDerivedValue: true,
      colSpan: 2,
    },
    // // previous columns
    // {
    //   key: "VariantAnalysisTablePPMPlaceholder.obDate",
    //   title: "Client Daily Campaign Amazon Table Date",
    //   shortTitle: "Date",
    //   type: "time",
    //   dataIndex: "VariantAnalysisTablePPMPlaceholder.obDate",
    // },
    {
      key: "VariantAnalysisTablePPMPlaceholder.BSR",
      type: "number",
      isSingleColumn: true,
      dataIndex: "VariantAnalysisTablePPMPlaceholder.BSR",
      title: "Best Seller Rank",
      shortTitle: "BSR",
      viewType: ["AGGREGATE"],
      colSpan: 1,
    },
    {
      key: "VariantAnalysisTablePPMPlaceholder.SalePrice",
      type: "number",
      isSingleColumn: true,
      dataIndex: "VariantAnalysisTablePPMPlaceholder.SalePrice",
      title: "Selling Price",
      shortTitle: "Selling Price",
      isCurrencyFormat: true,
      viewType: ["AGGREGATE"],
      colSpan: 1,
    },
    {
      key: "VariantAnalysisTablePPMPlaceholder.Quantity",
      type: "number",
      isSingleColumn: true,
      dataIndex: "VariantAnalysisTablePPMPlaceholder.Quantity",
      title: "Available Inventory",
      shortTitle: "Available Inventory",
      viewType: ["AGGREGATE"],
      colSpan: 1,
    },
    {
      key: "VariantAnalysisTablePPMPlaceholder.total_asp",
      type: "number",
      dataIndex: "VariantAnalysisTablePPMPlaceholder.total_asp",
      title: "Total ASP",
      shortTitle: "ASP",
      isCurrencyFormat: true,
      viewType: ["AGGREGATE"],
      colSpan: 2,
    },
    // {
    //   key: "VariantAnalysisTablePPMPlaceholder.roasDRR",
    //   type: "number",
    //   dataIndex: "VariantAnalysisTablePPMPlaceholder.roasDRR",
    //   title: "Variant Analysis Table ROAS (DRR)",
    //   shortTitle: "ROAS (DRR)",
    //   maximumFractionDigits: 1,
    //   viewType: ["DRR"],
    //   isDerivedValue: true,
    //   colSpan: 2,
    // },
    // {
    //   key: "VariantAnalysisTablePPMPlaceholder.acosDRR",
    //   type: "number",
    //   dataIndex: "VariantAnalysisTablePPMPlaceholder.acosDRR",
    //   title: "Variant Analysis Table ACOS (DRR)",
    //   shortTitle: "ACOS (DRR)",
    //   format: "percent",
    //   maximumFractionDigits: 0,
    //   viewType: ["DRR"],
    //   isDerivedValue: true,
    //   colSpan: 2,
    // },
  ],
  myntra: [
    {
      key: "product_id",
      innerKeys: {
        image: "",
        productName: "product_name_measures",
        asin: {label:"Style ID", value:"product_id"},

      },
      title: "Products",
      shortTitle: "Products",
      type: "string",
      // isSingleColumn is used to show the column in single column in comparison download
      isSingleColumn: true,
      dataIndex: "product_id",
      isSticky: true,
      stickyLeft: 0,
      minWidth: 240,
      isDimension: true,
      viewType: ["DRR","AGGREGATE", "FUNNEL", "PERFORMANCE", "ORDER"],
      alignment:"left",
      colSpan: 1,
    },
    ...platformWiseExtraColumnsConfig["myntra"],
    // {
    //   key: "product_name",
    //   title: "Variant Analysis Table Product Name",
    //   shortTitle: "Product Name",
    //   type: "string",
    //   isSingleColumn: true,
    //   dataIndex: "product_name",
    //   isSticky: true,
    //   stickyLeft: 180,
    //   minWidth: 150,
    //   isDimension: true,
    //   viewType: ["DRR","AGGREGATE", "FUNNEL", "PERFORMANCE"]
    // },
    {
      key: "spends",
      type: "number",
      dataIndex: "spends",
      title: "Spends",
      shortTitle: "Spends",
      maximumFractionDigits: 0,
      isCurrencyFormat: true,
      viewType: ["AGGREGATE", "PERFORMANCE", "FUNNEL", "ORDER"],
      colSpan: 2,
    },
    {
      key: "impressions",
      type: "number",
      dataIndex: "impressions",
      title: "Impressions",
      shortTitle: "Impressions",
      viewType: ["AGGREGATE", "PERFORMANCE", "FUNNEL"],
      colSpan: 2,
    },
    // {
    //   key: "views",
    //   type: "number",
    //   dataIndex: "views",
    //   title: "Views",
    //   shortTitle: "Views",
    //   viewType: ["AGGREGATE", "PERFORMANCE"]
    // },
    {
      key: "clicks",
      type: "number",
      dataIndex: "clicks",
      title: "Clicks",
      shortTitle: "Clicks",
      viewType: ["AGGREGATE", "PERFORMANCE", "FUNNEL"],
      colSpan: 2,
    },
     {
      key: "ctr",
      type: "number",
      format: "percent",
      dataIndex: "ctr",
      title: "CTR",
      shortTitle: "CTR",
      viewType: ["AGGREGATE", "PERFORMANCE", "FUNNEL"],
      isDerivedValue: true,
      colSpan: 2,
    },
    {
      key: "cpc",
      type: "number",
      dataIndex: "cpc",
      title: "CPC",
      shortTitle: "CPC",
      viewType: ["AGGREGATE", "PERFORMANCE"],
      isDerivedValue: true,
      colSpan: 2,
    },
     {
      key: "cvr",
      type: "number",
      format: "percent",
      dataIndex: "cvr",
      title: "CVR",
      shortTitle: "CVR",
      viewType: ["AGGREGATE", "PERFORMANCE", "FUNNEL"],
      isDerivedValue: true,
      colSpan: 2,
    },
      {
      key: "total_cvr", 
      type: "number",
      format: "percent",
      dataIndex: "total_cvr",
      title: "Total CVR",
      shortTitle: "Total CVR",
      viewType: ["AGGREGATE", "PERFORMANCE", "FUNNEL"],
      isDerivedValue: true,
      colSpan: 2,
    },
    {
      key: "units_sold_direct",
      type: "number",
      dataIndex: "units_sold_direct",
      title: "Units Direct",
      shortTitle: "Units Direct",
      viewType: ["AGGREGATE", "ORDER"],
      colSpan: 2,
    },
    {
      key: "units_sold_indirect",
      type: "number",
      dataIndex: "units_sold_indirect",
      title: "Units Indirect",
      shortTitle: "Units Indirect",
      viewType: ["AGGREGATE", "ORDER"],
      colSpan: 2,
    },
    {
      key: "orders",
      type: "number",
      dataIndex: "orders",
      title: "Ads Units",
      shortTitle: "Ads Units",
      viewType: ["AGGREGATE", "PERFORMANCE", "FUNNEL", "ORDER"],
      colSpan: 2,
    },
    {
      key: "order_units",
      type: "number",
      dataIndex: "order_units",
      title: "Total Units",
      shortTitle: "Total Units",
      viewType: ["AGGREGATE", "PERFORMANCE", "FUNNEL", "ORDER"],
      colSpan: 2,
    },
    {
      key: "partners_total_orders",
      type: "number",
      dataIndex: "partners_total_orders",
      title: "Total Units (Partners)",
      shortTitle: "Total Units (Partners)",
      viewType: ["AGGREGATE", "PERFORMANCE", "FUNNEL", "ORDER"],
      colSpan: 2,
    },
    {
      key: "revenue_direct",
      type: "number",
      dataIndex: "revenue_direct",
      title: "Revenue Direct",
      shortTitle: "Revenue Direct",
      isCurrencyFormat: true,
      maximumFractionDigits: 0,
      viewType: ["AGGREGATE", "ORDER"],
      isDerivedValue: true,
      colSpan: 2,
    },
    {
      key: "revenue_indirect",
      type: "number",
      dataIndex: "revenue_indirect",
      title: "Revenue Indirect",
      shortTitle: "Revenue Indirect",
      isCurrencyFormat: true,
      maximumFractionDigits: 0,
      viewType: ["AGGREGATE", "ORDER"],
      isDerivedValue: true,
      colSpan: 2,
    },
     {
      key: "revenue",
      type: "number",
      dataIndex: "revenue",
      title: "Ads Revenue",
      shortTitle: "Ads Revenue",
      isCurrencyFormat: true,
      maximumFractionDigits: 0,
      viewType: ["AGGREGATE", "PERFORMANCE", "FUNNEL", "ORDER"],
      colSpan: 2,
    },
    {
      key: "gmv",
      type: "number",
      dataIndex: "gmv",
      title: "Total Revenue (GMV)",
      shortTitle: "Total Revenue (GMV)",
      isCurrencyFormat: true,
      maximumFractionDigits: 0,
      viewType: ["AGGREGATE", "PERFORMANCE", "FUNNEL", "ORDER"],
      colSpan: 2,
    },
     {
      key: "direct_revenue_percentage",
      type: "number",
      format: "percent",
      dataIndex: "direct_revenue_percentage",
      title: "Revenue Direct (Percentage)",
      shortTitle: "Revenue Direct (Percentage)",
      maximumFractionDigits: 0,
      viewType: ["AGGREGATE"],
      isDerivedValue: true,
      colSpan: 2,
    },
    {
      key: "indirect_revenue_percentage",
      type: "number",
      format: "percent",
      dataIndex: "indirect_revenue_percentage",
      title: "Revenue Indirect (Percentage)",
      shortTitle: "Revenue Indirect (Percentage)",
      maximumFractionDigits: 0,
      viewType: ["AGGREGATE"],
      isDerivedValue: true,
      colSpan: 2,
    },
    {
      key: "partners_total_revenue",
      type: "number",
      dataIndex: "partners_total_revenue",
      title: "Total Revenue (Partners)",
      shortTitle: "Total Revenue (Partners)",
      isCurrencyFormat: true,
      maximumFractionDigits: 0,
      viewType: ["AGGREGATE","PERFORMANCE", "ORDER"],
      colSpan: 2,
    },
    {
      key: "total_inventory_count",
      type: "number",
      dataIndex: "total_inventory_count",
      title: "Total Inventory Count",
      shortTitle: "Total Inventory Count",
      viewType: ["AGGREGATE", "ORDER"],
      colSpan: 2,
    },
    {
      key: "direct_roas",
      type: "number",
      dataIndex: "direct_roas",
      title: "ROAS Direct",
      shortTitle: "ROAS Direct",
      viewType: ["AGGREGATE"],
      maximumFractionDigits: 1,
      isDerivedValue: true,
      colSpan: 2,
    },
    {
      key: "roas",
      type: "number",
      dataIndex: "roas",
      title: "Ads ROAS",
      shortTitle: "Ads ROAS",
      maximumFractionDigits: 1,
      viewType: ["AGGREGATE", "PERFORMANCE", "FUNNEL", "ORDER"],
      isDerivedValue: true,
      colSpan: 2,
    },
    {
      key: "total_roas",
      type: "number",
      dataIndex: "total_roas",
      title: "Total ROAS",
      shortTitle: "Total ROAS",
      maximumFractionDigits: 1,
      viewType: ["AGGREGATE", "PERFORMANCE", "FUNNEL", "ORDER"],
      isDerivedValue: true,
      colSpan: 2,
    },
    {
      key: "mrp",
      type: "number",
      isSingleColumn: true,
      dataIndex: "mrp",
      title: "Maximum Retail Price",
      shortTitle: "MRP",
      isCurrencyFormat: true,
      viewType: ["AGGREGATE", "ORDER"],
      colSpan: 1,
    },
    {
      key: "asp",
      type: "number",
      dataIndex: "asp",
      title: "ASP",
      shortTitle: "ASP",
      isCurrencyFormat: true,
      viewType: ["AGGREGATE", "ORDER"],
      isDerivedValue: true,
      colSpan: 2,
    },

    // {
    //   key: "spends_drr",
    //   type: "number",
    //   dataIndex: "spends_drr",
    //   title: "Spends (DRR)",
    //   shortTitle: "Spends (DRR)",
    //   maximumFractionDigits: 0,
    //   viewType: ["DRR"],
    //   colSpan: 2,
    // },
    // {
    //   key: "views_drr",
    //   type: "number",
    //   dataIndex: "views_drr",
    //   title: "Views (DRR)",
    //   shortTitle: "Views (DRR)",
    //   viewType: ["DRR"]
    // },
    // {
    //   key: "clicks_drr",
    //   type: "number",
    //   dataIndex: "clicks_drr",
    //   title: "Clicks (DRR)",
    //   shortTitle: "Clicks (DRR)",
    //   maximumFractionDigits: 0,
    //   viewType: ["DRR"],
    //   colSpan: 2,
    // },
    // {
    //   key: "ctr_drr",
    //   type: "number",
    //   format: "percent",
    //   dataIndex: "ctr_drr",
    //   title: "CTR (DRR)",
    //   shortTitle: "CTR (DRR)",
    //   viewType: ["DRR"],
    //   isDerivedValue: true,
    //   colSpan: 2,
    // },
    // {
    //   key: "cpc_drr",
    //   type: "number",
    //   dataIndex: "cpc_drr",
    //   title: "CPC (DRR)",
    //   shortTitle: "CPC (DRR)",
    //   viewType: ["DRR"],
    //   isDerivedValue: true,
    //   colSpan: 2,
    // },
    // {
    //   key: "cvr_drr",
    //   type: "number",
    //   format: "percent",
    //   dataIndex: "cvr_drr",
    //   title: "CVR (DRR)",
    //   shortTitle: "CVR (DRR)",
    //   viewType: ["DRR"],
    //   isDerivedValue: true,
    //   colSpan: 2,
    // },
    // {
    //   key: "units_sold_direct_drr",
    //   type: "number",
    //   dataIndex: "units_sold_direct_drr",
    //   title: "Units Direct (DRR)",
    //   shortTitle: "Units Direct (DRR)",
    //   maximumFractionDigits: 0,
    //   viewType: ["DRR"],
    //   colSpan: 2,
    // },
    // {
    //   key: "units_sold_indirect_drr",
    //   type: "number",
    //   dataIndex: "units_sold_indirect_drr",
    //   title: "Units Indirect (DRR)",
    //   shortTitle: "Units Indirect (DRR)",
    //   maximumFractionDigits: 0,
    //   viewType: ["DRR"],
    //   colSpan: 2,
    // },
    // {
    //   key: "revenue_direct_drr",
    //   type: "number",
    //   dataIndex: "revenue_direct_drr",
    //   title: "Revenue Direct (DRR)",
    //   shortTitle: "Revenue Direct (DRR)",
    //   isCurrencyFormat: true,
    //   maximumFractionDigits: 0,
    //   viewType: ["DRR"],
    //   isDerivedValue: true,
    //   colSpan: 2,
    // },
    // {
    //   key: "revenue_indirect_drr",
    //   type: "number",
    //   dataIndex: "revenue_indirect_drr",
    //   title: "Revenue Indirect (DRR)",
    //   shortTitle: "Revenue Indirect (DRR)",
    //   isCurrencyFormat: true,
    //   maximumFractionDigits: 0,
    //   viewType: ["DRR"],
    //   isDerivedValue: true,
    //   colSpan: 2,
    // },
    // {
    //   key: "roas_drr",
    //   type: "number",
    //   dataIndex: "roas_drr",
    //   title: "ROAS (DRR)",
    //   shortTitle: "ROAS (DRR)",
    //   viewType: ["DRR"],
    //   maximumFractionDigits: 1,
    //   isDerivedValue: true,
    //   colSpan: 2,
    // },
    // {
    //   key: "asp_drr",
    //   type: "number",
    //   dataIndex: "asp_drr",
    //   title: "ASP (DRR)",
    //   shortTitle: "ASP (DRR)",
    //   isCurrencyFormat: true,
    //   viewType: ["DRR"],
    //   isDerivedValue: true,
    //   colSpan: 2,
    // }
  ],
  flipkart: [
    {
      key: "product_id",
       innerKeys: {
        image: "",
        productName: "product_name",
        asin: {label:"FSN ID", value:"product_id"},

      },
      title: "Products",
      shortTitle: "Products",
      type: "string",
      // isSingleColumn is used to show the column in single column in comparison download
      isSingleColumn: true,
      dataIndex: "product_id",
      isDimension: true,
      isSticky: true,
      stickyLeft: 0,
      minWidth: 240,
      viewType: ["DRR","AGGREGATE", "FUNNEL", "PERFORMANCE", "ORDER"],
      alignment:"left",
      colSpan: 1,
      isDimension: true,
    },
    ...platformWiseExtraColumnsConfig["flipkart"],
    // {
    //   key: "product_name",
    //   title: "Variant Analysis Table Product Name",
    //   shortTitle: "Product Name",
    //   type: "string",
    //   isSingleColumn: true,
    //   dataIndex: "product_name",
    //   isSticky: true,
    //   stickyLeft: 180,
    //   minWidth: 150,
    //   viewType: ["DRR","AGGREGATE", "FUNNEL", "PERFORMANCE"]
    // },
    {
      key: "spends",
      type: "number",
      dataIndex: "spends",
      title: "Spends",
      shortTitle: "Spends",
      maximumFractionDigits: 0,
      isCurrencyFormat: true,
      viewType: ["AGGREGATE", "PERFORMANCE", "FUNNEL", "ORDER"],
      colSpan: 2,
    },
    {
      key: "impressions",
      type: "number",
      dataIndex: "impressions",
      title: "Impressions",
      shortTitle: "Impressions",
      viewType: ["AGGREGATE", "PERFORMANCE", "FUNNEL"],
      colSpan: 2,
    },
    {
      key: "cpm",
      type: "number",
      dataIndex: "cpm",
      title: "CPM",
      shortTitle: "CPM",
      viewType: ["AGGREGATE"],
      isDerivedValue: true,
      colSpan: 2,
    },
     {
      key: "clicks",
      type: "number",
      dataIndex: "clicks",
      title: "Clicks",
      shortTitle: "Clicks",
      viewType: ["AGGREGATE", "PERFORMANCE", "FUNNEL"],
      colSpan: 2,
    },
     {
      key: "ctr",
      type: "number",
      format: "percent",
      dataIndex: "ctr",
      title: "CTR",
      shortTitle: "CTR",
      viewType: ["AGGREGATE", "PERFORMANCE", "FUNNEL"],
      isDerivedValue: true,
      colSpan: 2,
    },
    {
      key: "cpc",
      type: "number",
      dataIndex: "cpc",
      title: "CPC",
      shortTitle: "CPC",
      viewType: ["AGGREGATE", "PERFORMANCE"],
      isDerivedValue: true,
      colSpan: 2,
    },
     {
      key: "cvr", 
      type: "number",
      format: "percent",
      dataIndex: "cvr",
      title: "Ads CVR",
      shortTitle: "Ads CVR",
      viewType: ["AGGREGATE", "PERFORMANCE", "FUNNEL"],
      isDerivedValue: true,
      colSpan: 2,
    },
     {
      key: "total_cvr", 
      type: "number",
      format: "percent",
      dataIndex: "total_cvr",
      title: "Total CVR",
      shortTitle: "Total CVR",
      viewType: ["AGGREGATE", "PERFORMANCE", "FUNNEL"],
      isDerivedValue: true,
      colSpan: 2,
    },
    {
      key: "units_sold_direct",
      type: "number",
      dataIndex: "units_sold_direct",
      title: "Units (Direct)",
      shortTitle: "Units (Direct)",
      viewType: ["AGGREGATE", "ORDER"],
      colSpan: 2,
    },
    {
      key: "units_sold_indirect",
      type: "number",
      dataIndex: "units_sold_indirect",
      title: "Units (Indirect)",
      shortTitle: "Units (Indirect)",
      viewType: ["AGGREGATE", "ORDER"],
      colSpan: 2,
    },
    {
      key: "orders",
      type: "number",
      dataIndex: "orders",
      title: "Ads Units",
      shortTitle: "Ads Units",
      viewType: ["AGGREGATE", "PERFORMANCE", "FUNNEL", "ORDER"],
      colSpan: 2,
    },
    {
      key: "order_units",
      type: "number",
      dataIndex: "order_units",
      title: "Total Units",
      shortTitle: "Total Units",
      viewType: ["AGGREGATE", "PERFORMANCE", "FUNNEL", "ORDER"],
      colSpan: 2,
    },
    {
      key: "revenue_direct",
      type: "number",
      dataIndex: "revenue_direct",
      title: "Revenue (Direct)",
      shortTitle: "Revenue (Direct)",
      isCurrencyFormat: true,
      maximumFractionDigits: 0,
      viewType: ["AGGREGATE", "ORDER"],
      isDerivedValue: true,
      colSpan: 2,
    },
    {
      key: "revenue_indirect",
      type: "number",
      dataIndex: "revenue_indirect",
      title: "Revenue (Indirect)",
      shortTitle: "Revenue (Indirect)",
      isCurrencyFormat: true,
      maximumFractionDigits: 0,
      viewType: ["AGGREGATE", "ORDER"],
      isDerivedValue: true,
      colSpan: 2,
    },
    {
      key: "revenue",
      type: "number",
      dataIndex: "revenue",
      title: "Ads Revenue",
      shortTitle: "Ads Revenue",
      isCurrencyFormat: true,
      maximumFractionDigits: 0,
      viewType: ["AGGREGATE", "PERFORMANCE", "FUNNEL", "ORDER"],
      colSpan: 2,
    },
    {
      key: "gmv",
      type: "number",
      dataIndex: "gmv",
      title: "Total Revenue (GMV)",
      shortTitle: "Total Revenue (GMV)",
      isCurrencyFormat: true,
      maximumFractionDigits: 0,
      viewType: ["AGGREGATE", "PERFORMANCE", "FUNNEL", "ORDER"],
      colSpan: 2,
    },
    {
      key: "direct_revenue_percentage",
      type: "number",
      format: "percent",
      dataIndex: "direct_revenue_percentage",
      title: "Revenue Direct (Percentage)",
      shortTitle: "Revenue Direct (Percentage)",
      maximumFractionDigits: 0,
      viewType: ["AGGREGATE"],
      isDerivedValue: true,
      colSpan: 2,
    },
    {
      key: "indirect_revenue_percentage",
      type: "number",
      format: "percent",
      dataIndex: "indirect_revenue_percentage",
      title: "Revenue Indirect (Percentage)",
      maximumFractionDigits: 0,
      shortTitle: "Revenue Indirect (Percentage)",
      viewType: ["AGGREGATE"],
      isDerivedValue: true,
      colSpan: 2,
    },
    {
      key: "direct_roas",
      type: "number",
      dataIndex: "direct_roas",
      title: "ROAS Direct",
      shortTitle: "ROAS Direct",
      viewType: ["AGGREGATE"],
      maximumFractionDigits: 1,
      isDerivedValue: true,
      colSpan: 2,
    },
    {
      key: "roas",
      type: "number",
      dataIndex: "roas",
      title: "Ads ROAS",
      shortTitle: "Ads ROAS",
      maximumFractionDigits: 1,
      viewType: ["AGGREGATE", "PERFORMANCE", "FUNNEL", "ORDER"],
      isDerivedValue: true,
      colSpan: 2,
    },
    {
      key: "total_roas",
      type: "number",
      dataIndex: "total_roas",
      title: "Total ROAS",
      shortTitle: "Total ROAS",
      maximumFractionDigits: 1,
      viewType: ["AGGREGATE", "PERFORMANCE", "FUNNEL", "ORDER"],
      isDerivedValue: true,
      colSpan: 2,
    },
    {
      key: "aov",
      type: "number",
      dataIndex: "aov",
      title: "Average Order Value",
      shortTitle: "Average Order Value",
      viewType: ["AGGREGATE"],
      isDerivedValue: true,
      colSpan: 2,
    },
    {
      key: "acos",
      type: "number",
      dataIndex: "acos",
      title: "ACOS",
      format: "percent",
      shortTitle: "ACOS",
      maximumFractionDigits: 0,
      viewType: ["AGGREGATE"],
      isDerivedValue: true,
      colSpan: 2,
    },
    //  {
    //   key: "spends_drr",
    //   type: "number",
    //   dataIndex: "spends_drr",
    //   title: "Spends (DRR)",
    //   shortTitle: "Spends (DRR)",
    //   isCurrencyFormat: true,
    //   maximumFractionDigits: 0,
    //   viewType: ["DRR"],
    //   colSpan: 2,
    // },
    // {
    //   key: "clicks_drr",
    //   type: "number",
    //   dataIndex: "clicks_drr",
    //   title: "Clicks (DRR)",
    //   shortTitle: "Clicks (DRR)",
    //   maximumFractionDigits: 0,
    //   viewType: ["DRR"],
    //   colSpan: 2,
    // },
    // {
    //   key: "ctr_drr",
    //   type: "number",
    //   format: "percent",
    //   dataIndex: "ctr_drr",
    //   title: "CTR (DRR)",
    //   shortTitle: "CTR (DRR)",
    //   viewType: ["DRR"],
    //   isDerivedValue: true,
    //   colSpan: 2,
    // },
    //  {
    //   key: "cpc_drr",
    //   type: "number",
    //   dataIndex: "cpc_drr",
    //   title: "CPC (DRR)",
    //   shortTitle: "CPC (DRR)",
    //   viewType: ["DRR"],
    //   isDerivedValue: true,
    //   colSpan: 2,
    // },
    // {
    //   key: "cvr_drr",
    //   type: "number",
    //   format: "percent",
    //   dataIndex: "cvr_drr",
    //   title: "CVR (DRR)",
    //   shortTitle: "CVR (DRR)",
    //   viewType: ["DRR"],
    //   isDerivedValue: true,
    //   colSpan: 2,
    // },
   
    // {
    //   key: "units_sold_drr",
    //   type: "number",
    //   dataIndex: "units_sold_drr",
    //   title: "Units (DRR)",
    //   shortTitle: "Units (DRR)",
    //   maximumFractionDigits: 0,
    //   viewType: ["DRR"],
    //   colSpan: 2,
    // },
    // {
    //   key: "units_sold_direct_drr",
    //   type: "number",
    //   dataIndex: "units_sold_direct_drr",
    //   title: "Units (Direct) (DRR)",
    //   maximumFractionDigits: 0,
    //   shortTitle: "Units (Direct) (DRR)",
    //   viewType: ["DRR"],
    //   colSpan: 2,
    // },
    // {
    //   key: "units_sold_indirect_drr",
    //   type: "number",
    //   dataIndex: "units_sold_indirect_drr",
    //   title: "Units (Indirect) (DRR)",
    //   shortTitle: "Units (Indirect) (DRR)",
    //   maximumFractionDigits: 0,
    //   viewType: ["DRR"],
    //   colSpan: 2,
    // },
    // {
    //   key: "revenue_direct_drr",
    //   type: "number",
    //   dataIndex: "revenue_direct_drr",
    //   title: "Revenue (Direct) (DRR)",
    //   shortTitle: "Revenue (Direct) (DRR)",
    //   isCurrencyFormat: true,
    //   maximumFractionDigits: 0,
    //   viewType: ["DRR"],
    //   isDerivedValue: true,
    //   colSpan: 2,
    // },
    // {
    //   key: "revenue_indirect_drr",
    //   type: "number",
    //   dataIndex: "revenue_indirect_drr",
    //   title: "Revenue (Indirect) (DRR)",
    //   shortTitle: "Revenue (Indirect) (DRR)",
    //   isCurrencyFormat: true,
    //   maximumFractionDigits: 0,
    //   viewType: ["DRR"],
    //   isDerivedValue: true,
    //   colSpan: 2,
    // },
    //  {
    //   key: "revenue_drr",
    //   type: "number",
    //   dataIndex: "revenue_drr",
    //   title: "Revenue (DRR)",
    //   shortTitle: "Revenue (DRR)",
    //   isCurrencyFormat: true,
    //   maximumFractionDigits: 0,
    //   viewType: ["DRR"],
    //   colSpan: 2,
    // },
    // {
    //   key: "roas_drr",
    //   type: "number",
    //   dataIndex: "roas_drr",
    //   title: "ROAS (DRR)",
    //   shortTitle: "ROAS (DRR)",
    //   viewType: ["DRR"],
    //   maximumFractionDigits: 1,
    //   isDerivedValue: true,
    //   colSpan: 2,
    // }
  ],
};

const getMappedCustomAttributeSourceName = (columnKey, customAttributeMapping = {}) => {
  return customAttributeMapping?.[columnKey]?.source_attribute_name;
};

export const getMappedCustomAttributeLabel = (
  columnKey,
  hardcodedLabel,
  customAttributeMapping = {}
) => {
  const sourceAttributeName = getMappedCustomAttributeSourceName(
    columnKey,
    customAttributeMapping
  );
  if (!sourceAttributeName) return hardcodedLabel;
  if (hardcodedLabel?.includes(`(${sourceAttributeName})`)) return hardcodedLabel;
  return `${hardcodedLabel} (${sourceAttributeName})`;
};

const mergeCustomAttributeLabelInColumn = (column, customAttributeMapping = {}) => {
  const mappedShortTitle = getMappedCustomAttributeLabel(
    column?.key,
    column?.shortTitle,
    customAttributeMapping
  );
  const mappedTitle = getMappedCustomAttributeLabel(
    column?.key,
    column?.title,
    customAttributeMapping
  );

  return {
    ...column,
    shortTitle: mappedShortTitle,
    title: mappedTitle,
  };
};


export const getColumnsConfig = (
  visibleColumns,
  isComparisonApi = false,
  customAttributeMapping = {}
) => {
  const userPlatform = JSON.parse(localStorage.getItem("user_platform"));
  let columnConfig = platformWiseColumnConfig[userPlatform];
  //first map then filter because we need to replace the tableName first and then filter the visible columns
  return columnConfig
    .map((column) => {
      const mergedColumn = mergeCustomAttributeLabelInColumn(
        column,
        customAttributeMapping
      );
      return {
        ...mergedColumn,
        key: mergedColumn.key,
        dataIndex: `${
          !mergedColumn?.isSingleColumn && isComparisonApi
            ? "startDate-endDate,"
            : ""
        }${
          mergedColumn.dataIndex
        }`,
      };
    })
    .filter((column) => {
      return visibleColumns.includes(column.key);
    });
};

export const getAllColumns = (
  isComparisonApi = false,
  dimensions = [],
  customAttributeMapping = {}
) => {
  const userPlatform = JSON.parse(localStorage.getItem("user_platform"));
  let columnConfig = platformWiseColumnConfig[userPlatform];

  const dimensionOrderMap = new Map(
    (dimensions || []).map((dimension, index) => [dimension, index])
  );

  const updatedAndSortedAndFilteredColumnConfig = [...columnConfig]
    .sort((a, b) => {
      const aOrder = dimensionOrderMap.has(a.key)
        ? dimensionOrderMap.get(a.key)
        : Number.MAX_SAFE_INTEGER;
      const bOrder = dimensionOrderMap.has(b.key)
        ? dimensionOrderMap.get(b.key)
        : Number.MAX_SAFE_INTEGER;

      if (aOrder !== bOrder) {
        return aOrder - bOrder;
      }
      return 0;
    })
    .filter((column) => {
      if (!dimensions.includes(column.key) && column.isDimension) {
        return false;
      }
      return true;
    });

  let cumulativeStickyLeft = 0;

  return updatedAndSortedAndFilteredColumnConfig.map((column, index) => {
    const minWidth = column.minWidth || 120;
    const mergedColumn = mergeCustomAttributeLabelInColumn(
      column,
      customAttributeMapping
    );

    const updatedColumn = {
      ...mergedColumn,
      stickyLeft: index === 0 ? 0 : cumulativeStickyLeft,
      key: mergedColumn.key,
      dataIndex: `${
        !mergedColumn?.isSingleColumn && isComparisonApi
          ? "startDate-endDate,"
          : ""
      }${mergedColumn.dataIndex}`,
    };

    // increment cumulative width AFTER assigning stickyLeft
    cumulativeStickyLeft += minWidth;

    return updatedColumn;
  });
}

const dashboardColumns = [
  "spends",
  "clicks",
  "ctr",
  "cpc",
  "cvr",
  "total_cvr",
  "orders",
  "order_units",
  "revenue",
  "gmv",
  "roas",
  "total_roas"
];

export const getDashboardColumnsWithoutDimensions = () => {
  const userPlatform = JSON.parse(localStorage.getItem("user_platform"));
  const columnConfig = platformWiseColumnConfig[userPlatform];
  return columnConfig?.filter((column) => {
    return dashboardColumns.includes(column.key) && !column.isDimension;
  }) ?? [];
}

// for home dashboard new
export const getAllColumnsWithoutDimensions = () => {
  const userPlatform = JSON.parse(localStorage.getItem("user_platform")); 
  const columnConfig = platformWiseColumnConfig[userPlatform];
  return columnConfig?.filter((column) => {
    return !column.isDimension;
  }) ?? [];
};